import {
    ABTestConfig,
    CampaignChannels,
    ExitCriteriaFields,
    ReEntryFields,
    SendRecurrenceFrequency,
    SplitActions,
    SplitTypes,
} from 'domains/campaigns/types';
import { SegmentDefinition, SegmentFilterCategorySpecificField, SegmentFilterState } from 'domains/segments/types';

import { CampaignStateTypes } from './customCampaignReducer';

export enum actionTypes {
    SET_NAME = 'SET_NAME',
    SET_SEND_NAME = 'SET_SEND_NAME',
    SELECT_AUDIENCE = 'SELECT_AUDIENCE',
    SET_CALLER_PROFILE_ID = 'SET_CALLER_PROFILE_ID',
    SET_SMS_SENDER_PROFILE_ID = 'SET_SMS_SENDER_PROFILE_ID',
    SET_COHORT_ID = 'SET_COHORT_ID',
    SET_DIRECT_MAIL_CONTENT = 'SET_DIRECT_MAIL_CONTENT',
    SET_VOICE_CALL_CONTENT = 'SET_VOICE_CALL_CONTENT',
    SET_SMS_CONTENT = 'SET_SMS_CONTENT',
    SET_TEMPLATE_ID = 'SET_TEMPLATE_ID',
    SET_SENDER_PROFILE_ID = 'SET_SENDER_PROFILE_ID',
    SET_SUBJECT_LINE = 'SET_SUBJECT_LINE',
    SET_PRE_HEADER = 'SET_PRE_HEADER',
    SET_SCHEDULED_TIMESTAMP = 'SET_SCHEDULED_TIMESTAMP',
    SET_HAS_RECOMMENDED_TIME = 'SET_HAS_RECOMMENDED_TIME',
    SET_RECURRENCE_FREQUENCY = 'SET_RECURRENCE_FREQUENCY',
    SET_RECURRENCE_DAY_OF_WEEK = 'SET_RECURRENCE_DAY_OF_WEEK',
    SET_RECURRENCE_OCCURRENCE_POSITION = 'SET_RECURRENCE_OCCURRENCE_POSITION',
    SET_SCHEDULED_END_TIMESTAMP = 'SET_SCHEDULED_END_TIMESTAMP',
    SET_PARENT_AUDIENCE_DELAY_INTERVAL = 'SET_PARENT_AUDIENCE_DELAY_INTERVAL',
    SET_RE_ENTRY_DETAILS = 'SET_RE_ENTRY_DETAILS',
    CLEAR_RE_ENTRY_DETAILS = 'CLEAR_RE_ENTRY_DETAILS',
    SET_EXIT_CRITERIA = 'SET_EXIT_CRITERIA',
    CLEAR_EXIT_CRITERIA = 'CLEAR_EXIT_CRITERIA',
    ADD_CONDITIONAL_SPLIT_SENDS = 'ADD_CONDITIONAL_SPLIT_SENDS',
    ADD_RANDOM_SPLIT_SENDS = 'ADD_RANDOM_SPLIT_SENDS',
    SET_SEGMENT = 'SET_SEGMENT',
    SET_SPLIT_SEGMENT_DEFINITION = 'SET_SPLIT_SEGMENT_DEFINITION',
    SET_SPLIT_ACTION = 'SET_SPLIT_ACTION',
    SET_AB_TEST_CONFIG = 'SET_AB_TEST_CONFIG',
    SET_AB_TEST_PERCENTAGE = 'SET_AB_TEST_PERCENTAGE',
    UPDATE_COMPARISON_FIELDS = 'UPDATE_COMPARISON_FIELDS',
    REMOVE_AB_TEST_CONFIG = 'REMOVE_AB_TEST_CONFIG',
    ADD_SEND = 'ADD_SEND',
    ADD_BRANCH_SEND = 'ADD_BRANCH_SEND',
    REMOVE_SEND = 'REMOVE_SEND',
    REMOVE_BRANCH_DESCENDANTS = 'REMOVE_BRANCH_DESCENDANTS',
    REMOVE_CONDITIONAL_SPLIT = 'REMOVE_CONDITIONAL_SPLIT',
    REMOVE_RANDOM_SPLIT = 'REMOVE_RANDOM_SPLIT',
    RESET_CONDITIONAL_SPLIT_SEND = 'RESET_CONDITIONAL_SPLIT_SEND',
    RESET_CONTENT = 'RESET_CONTENT',
    RESET_SENDS = 'RESET_SENDS',
    SET_SEND_CHANNEL = 'SET_SEND_CHANNEL',
    SET_DRAFT_STATE = 'SET_DRAFT_STATE',
}

// Actions
export const setName = (name: string) => ({ type: actionTypes.SET_NAME, name });

export const setSendName = (name: string, sendIndex: number) => ({
    type: actionTypes.SET_SEND_NAME,
    parameters: { name, sendIndex },
});

export const selectAudience = (selectedAudienceId: number | null) => ({
    type: actionTypes.SELECT_AUDIENCE,
    selectedAudienceId,
});

export const setCohortId = (cohortId: string) => ({
    type: actionTypes.SET_COHORT_ID,
    parameters: { cohortId },
});

export const setDirectMailContent = (
    { contentId, contentName }: { contentId: number | null; contentName: string },
    sendIndex: number
) => ({
    type: actionTypes.SET_DIRECT_MAIL_CONTENT,
    parameters: { contentId, contentName, sendIndex },
});

export const setSmsContent = (
    { contentId, contentName }: { contentId: number | null; contentName: string },
    sendIndex: number
) => ({
    type: actionTypes.SET_SMS_CONTENT,
    parameters: { contentId, contentName, sendIndex },
});

export const setVoiceCallContent = (
    { contentId, contentName }: { contentId: number | null; contentName: string },
    sendIndex: number
) => ({
    type: actionTypes.SET_VOICE_CALL_CONTENT,
    parameters: { contentId, contentName, sendIndex },
});

export const setTemplateId = (templateId: number | null, sendIndex: number) => ({
    type: actionTypes.SET_TEMPLATE_ID,
    parameters: { templateId, sendIndex },
});

export const setSenderProfileId = (senderProfileId: number | null, sendIndex: number) => ({
    type: actionTypes.SET_SENDER_PROFILE_ID,
    parameters: { sendIndex, senderProfileId },
});

export const setSubjectLine = (subjectLine: string, sendIndex: number) => ({
    type: actionTypes.SET_SUBJECT_LINE,
    parameters: { sendIndex, subjectLine },
});

export const setPreHeader = (preHeader: string, sendIndex: number) => ({
    type: actionTypes.SET_PRE_HEADER,
    parameters: { preHeader, sendIndex },
});

export const setScheduledTimestamp = (scheduledTimestamp: string, sendIndex: number) => ({
    type: actionTypes.SET_SCHEDULED_TIMESTAMP,
    parameters: { scheduledTimestamp, sendIndex },
});

export const setHasRecommendedTime = (hasRecommendedTime: boolean, sendIndex: number = 0) => ({
    type: actionTypes.SET_HAS_RECOMMENDED_TIME,
    parameters: { hasRecommendedTime, sendIndex },
});

export const setRecurrenceFrequency = (recurrenceFrequency: SendRecurrenceFrequency) => ({
    type: actionTypes.SET_RECURRENCE_FREQUENCY,
    parameters: { recurrenceFrequency },
});

export const setRecurrenceDayOfWeek = (recurrenceDayOfWeek: string | null) => ({
    type: actionTypes.SET_RECURRENCE_DAY_OF_WEEK,
    parameters: { recurrenceDayOfWeek },
});

export const setRecurrenceOccurrencePosition = (recurrenceOccurrencePosition: number | null) => ({
    type: actionTypes.SET_RECURRENCE_OCCURRENCE_POSITION,
    parameters: { recurrenceOccurrencePosition },
});

export const setScheduledEndTimestamp = (scheduledEndTimestamp: string) => ({
    type: actionTypes.SET_SCHEDULED_END_TIMESTAMP,
    parameters: { scheduledEndTimestamp },
});

export const setParentAudienceDelayInterval = (parentAudienceDelayInterval: string, sendIndex: number) => ({
    type: actionTypes.SET_PARENT_AUDIENCE_DELAY_INTERVAL,
    parameters: { parentAudienceDelayInterval, sendIndex },
});

export const addConditionalSplitSends = (sendIndex: number) => ({
    type: actionTypes.ADD_CONDITIONAL_SPLIT_SENDS,
    parameters: { sendIndex },
});

export const addRandomSplitSends = (parentIndex: number) => ({
    type: actionTypes.ADD_RANDOM_SPLIT_SENDS,
    parameters: { parentIndex },
});

export const removeRandomSplit = (parentIndex: number) => ({
    type: actionTypes.REMOVE_RANDOM_SPLIT,
    parameters: { parentIndex },
});

export const setSegment = (segment: SegmentFilterState<SegmentFilterCategorySpecificField>, sendIndex: number) => ({
    type: actionTypes.SET_SEGMENT,
    parameters: { segment, sendIndex },
});

/**
 * Sets a conditional split's criteria directly on the MEETS send at `sendIndex`; the reducer also
 * writes the negated definition onto its DOES_NOT_MEET sibling (found by `splitNodeId`/`parentIndex`,
 * not by array position — see `findDoesNotMeetSiblingIndex`). Used for every split, root or nested —
 * `splitNodesSlice`/`EDIT_SPLIT_NODE` is unrelated Redux-side state for the legacy ConditionalSplitPage
 * and has no case in this reducer, so it's not a valid route here.
 */
export const setSplitSegmentDefinition = (sendIndex: number, segmentDefinition: SegmentDefinition) => ({
    type: actionTypes.SET_SPLIT_SEGMENT_DEFINITION,
    parameters: { sendIndex, segmentDefinition },
});

export const setSplitAction = (sendIndex: number, splitAction: SplitActions) => ({
    type: actionTypes.SET_SPLIT_ACTION,
    parameters: { sendIndex, splitAction },
});

export const addSend = (parentIndex: number, channel: CampaignChannels) => ({
    type: actionTypes.ADD_SEND,
    parameters: { parentIndex, channel },
});

export const addBranchSend = (parentIndex: number, splitType: SplitTypes, channel: CampaignChannels) => ({
    type: actionTypes.ADD_BRANCH_SEND,
    parameters: { parentIndex, splitType, channel },
});

export const removeSend = () => ({ type: actionTypes.REMOVE_SEND });

export const removeBranchDescendants = (sendIndex: number) => ({
    type: actionTypes.REMOVE_BRANCH_DESCENDANTS,
    parameters: { sendIndex },
});

export const removeConditionalSplit = (sendIndex: number) => ({
    type: actionTypes.REMOVE_CONDITIONAL_SPLIT,
    parameters: { sendIndex },
});

export const resetConditionalSplitSend = (sendIndex: number) => ({
    type: actionTypes.RESET_CONDITIONAL_SPLIT_SEND,
    parameters: { sendIndex },
});

export const resetContent = (sendIndex: number) => ({
    type: actionTypes.RESET_CONTENT,
    parameters: { sendIndex },
});

export const setReEntryDetails = (reentryDetails: ReEntryFields) => ({
    type: actionTypes.SET_RE_ENTRY_DETAILS,
    parameters: { reentryDetails },
});

export const clearReEntryDetails = () => ({
    type: actionTypes.CLEAR_RE_ENTRY_DETAILS,
});

export const setExitCriteria = (definition: ExitCriteriaFields) => ({
    type: actionTypes.SET_EXIT_CRITERIA,
    parameters: { exitCriteria: definition },
});

export const clearExitCriteria = () => ({
    type: actionTypes.CLEAR_EXIT_CRITERIA,
});

export const setCallerProfileId = (callerProfileId: number | null, sendIndex: number) => ({
    type: actionTypes.SET_CALLER_PROFILE_ID,
    parameters: { sendIndex, callerProfileId },
});

export const setSmsSenderProfileId = (smsSenderProfileId: number | null, sendIndex: number) => ({
    type: actionTypes.SET_SMS_SENDER_PROFILE_ID,
    parameters: { sendIndex, smsSenderProfileId },
});

export const setABTestConfig = (abTestConfig: ABTestConfig | null) => ({
    type: actionTypes.SET_AB_TEST_CONFIG,
    parameters: { abTestConfig },
});

export const setABTestPercentage = (testPercentage: number) => ({
    type: actionTypes.SET_AB_TEST_PERCENTAGE,
    parameters: { testPercentage },
});

export const updateComparisonFields = () => ({
    type: actionTypes.UPDATE_COMPARISON_FIELDS,
    parameters: {},
});

export const removeABTestConfig = () => ({
    type: actionTypes.REMOVE_AB_TEST_CONFIG,
    parameters: {},
});

export const resetSends = () => ({
    type: actionTypes.RESET_SENDS,
    parameters: {},
});

export const setSendChannel = (sendIndex: number, channel: CampaignChannels) => ({
    type: actionTypes.SET_SEND_CHANNEL,
    parameters: { sendIndex, channel },
});

export const setDraftState = (draftState: CampaignStateTypes) => ({
    type: actionTypes.SET_DRAFT_STATE,
    draftState,
});
